import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Paid as PaidIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Store as StoreIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseHandleCustomers } from "0.Mine/1.Hooks/CRM/Customers";
import { Customer, Service as ServiceType } from "0.Mine/3.Interfaces/customer";
import { CustomerDetails } from "0.Mine/3.Interfaces/customerDetails";
import { SafeArgonBox, SafeArgonTypography, SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";
import showConfirmationAlert from "layouts/pages/sweet-alerts/components/WithSuccessAttachedFunction/CancelSomething";

import CustomerDetailsView from "./CustomerDetails";
import { ServiceCard } from "./ServiceCard";
interface BookingDetailViewProps {
  booking: Customer | null;
  open: boolean;
  onClose: () => void;
  businessId: number;
  deleteBookingFromTable: (bookingId: number) => void;
}

export const BookingDetailView: React.FC<BookingDetailViewProps> = ({
  booking,
  open,
  onClose,
  businessId,
  deleteBookingFromTable,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>();
  const { getOneCustomerDetails, cancelBooking } = UseHandleCustomers();

  const closeAndCancelAppointment = async () => {
    try {
      const confirmed = await showConfirmationAlert({
        title: "Cancella appuntamento?",
        text: "Vuoi cancellare questo appuntamento? Questo mandera una mail automatica al cliente.",
        icon: "warning",
        confirmButtonText: "Si, cancella",
        cancelButtonText: "No, non cancellare",
        successTitle: "Appuntamento cancellato!",
        successText: "Appuntamento cancellato con successo.",
        onConfirm: async () => {
          // Perform the actual cancellation
          await cancelBooking(businessId, booking.booking_id);
        },
      });

      // If the booking was successfully canceled, close the dialog
      if (confirmed) {
        deleteBookingFromTable(booking.booking_id);
        onClose();
      }
    } catch (error) {
      console.error("Error in cancel booking process:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (booking && businessId) {
        const data = await getOneCustomerDetails(businessId, booking.client_id);
        setCustomerDetails(data);
      }
    };

    fetchData();
  }, [booking, businessId]);

  if (!booking) return null;

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMMM d, yyyy");
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    try {
      return format(parseISO(timeString), "h:mm a");
    } catch (error) {
      return timeString;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMMM d, yyyy • h:mm a");
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg" // Changed from "md" to "lg" to make dialog wider
      scroll="paper"
      aria-labelledby="booking-detail-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 10,
          bgcolor: alpha(theme.palette.background.default, 0.9),
          height: "85vh", // Set a fixed height to make dialog taller
          maxHeight: "900px",
          width: "90vw", // Set a fixed width to make dialog wider
          maxWidth: "1200px",
        },
      }}
    >
      <DialogTitle id="booking-detail-dialog-title" sx={{ p: 3, pb: 1 }}>
        <SafeArgonBox
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <SafeArgonTypography variant="h5" fontWeight="bold">
            {t("detailView.booking")} #{booking.booking_id}
          </SafeArgonTypography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </SafeArgonBox>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Customer Information - First column */}
          <Grid item xs={12} md={4} lg={3}>
            {" "}
            {/* Adjusted for 3-column layout */}
            <SafeArgonBox
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <SafeArgonBox
                sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}
              >
                {booking.pic ? (
                  <Avatar
                    src={booking.pic}
                    alt={`${booking.name} ${booking.surname}`}
                    sx={{ width: 80, height: 80, mb: 1 }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mb: 1,
                      fontSize: "2rem",
                      bgcolor: theme.palette.primary.main,
                    }}
                  >
                    {booking.name.charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <SafeArgonTypography variant="h6" fontWeight="bold" align="center">
                  {booking.name} {booking.surname}
                </SafeArgonTypography>
                <SafeArgonTypography variant="body2" color="text.secondary" align="center">
                  Customer ID: {booking.client_id}
                </SafeArgonTypography>
              </SafeArgonBox>

              <Divider sx={{ mb: 2 }} />

              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <EmailIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={booking.email}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>

                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PersonIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Customer since"
                    secondary={formatDate(booking.customerRegistration)}
                    primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    secondaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              </List>
            </SafeArgonBox>
          </Grid>

          {/* Appointment Details - Second column */}
          <Grid item xs={12} md={4} lg={5}>
            {" "}
            {/* Adjusted for 3-column layout */}
            <SafeArgonBox
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                mb: 3,
              }}
            >
              <SafeArgonTypography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {t("detailView.bookingDetails")}
              </SafeArgonTypography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SafeArgonBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CalendarIcon color="primary" sx={{ mr: 1 }} />
                    <SafeArgonBox>
                      <SafeArgonTypography variant="body2" color="text.secondary">
                        {t("detailView.date")}
                      </SafeArgonTypography>
                      <SafeArgonTypography variant="body1" fontWeight={500}>
                        {formatDate(booking.booking_date)}
                      </SafeArgonTypography>
                    </SafeArgonBox>
                  </SafeArgonBox>
                </Grid>

                {booking.serviceDetails.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <SafeArgonBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <TimeIcon color="primary" sx={{ mr: 1 }} />
                      <SafeArgonBox>
                        <SafeArgonTypography variant="body2" color="text.secondary">
                          {t("detailView.time")}
                        </SafeArgonTypography>
                        <SafeArgonTypography variant="body1" fontWeight={500}>
                          {formatTime(booking.serviceDetails[0].details.time_start)} -{" "}
                          {formatTime(booking.serviceDetails[0].details.time_end)}
                        </SafeArgonTypography>
                      </SafeArgonBox>
                    </SafeArgonBox>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <SafeArgonBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PaidIcon color="primary" sx={{ mr: 1 }} />
                    <SafeArgonBox>
                      <SafeArgonTypography variant="body2" color="text.secondary">
                        {t("detailView.totalPaid")}
                      </SafeArgonTypography>
                      <SafeArgonTypography variant="body1" fontWeight={500} color="success.main">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(booking.totalPaid)}
                      </SafeArgonTypography>
                    </SafeArgonBox>
                  </SafeArgonBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SafeArgonBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EventIcon color="primary" sx={{ mr: 1 }} />
                    <SafeArgonBox>
                      <SafeArgonTypography variant="body2" color="text.secondary">
                        {t("detailView.created")}
                      </SafeArgonTypography>
                      <SafeArgonTypography variant="body1">
                        {formatDateTime(booking.creation_date)}
                      </SafeArgonTypography>
                    </SafeArgonBox>
                  </SafeArgonBox>
                </Grid>
              </Grid>
            </SafeArgonBox>
            {/* Services */}
            <SafeArgonBox
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {t("detailView.services")}
              </Typography>

              {booking.serviceDetails.length > 0 ? (
                <SafeArgonBox>
                  {booking.serviceDetails[0].details.services.map((service, index) => (
                    <ServiceCard key={index} service={service} />
                  ))}
                </SafeArgonBox>
              ) : (
                <SafeArgonTypography variant="body2" color="text.secondary">
                  {t("detailView.noServiceDetailsAvailable")}
                </SafeArgonTypography>
              )}
            </SafeArgonBox>
          </Grid>

          {/* Third Column - New Content */}
          <Grid item xs={12} md={4} lg={4}>
            <SafeArgonBox
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <SafeArgonTypography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {t("detailView.customerInformation")}
              </SafeArgonTypography>

              {/* Placeholder content - to be filled later */}
              <SafeArgonBox sx={{ height: "100%", minHeight: "400px" }}>
                {/* Show loading indicator while customer details are being fetched */}
                {!customerDetails ? (
                  <SafeArgonBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      minHeight: "300px",
                    }}
                  >
                    <CircularProgress sx={{ mb: 2 }} />
                    <SafeArgonTypography variant="body2" color="text.secondary">
                      {t("detailView.loadingInfo")}
                    </SafeArgonTypography>
                  </SafeArgonBox>
                ) : (
                  <CustomerDetailsView customerDetails={customerDetails} />
                )}
              </SafeArgonBox>
            </SafeArgonBox>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <SafeArgonButton
          startIcon={<DeleteIcon />}
          color="error"
          onClick={closeAndCancelAppointment}
        >
          {t("detailView.cancelBooking")}
        </SafeArgonButton>
        <SafeArgonButton variant="contained" startIcon={<EditIcon />} onClick={onClose}>
          {t("detailView.editBooking")}
        </SafeArgonButton>
      </DialogActions>
    </Dialog>
  );
};
